import { useTranslation } from "react-i18next";
import { validateExactMatch, RecoveryPhraseType } from "@/schemas/recoveryKey";

import { FormStepProps } from "@/components/shared/Wizard/Wizard";
import { PHRASE_WORDS_COUNT } from "@/config/constants";
import { useToast } from "@/hooks/useToast";

import { encryptDataWithNewPassword } from "@/middleware/changePassword";
import BaseConfirmPhraseStep from "../Common/BaseConfirmPhraseStep";

export default function ConfirmPhraseChangeStep({
  onPrevious,
  onClose,
  formData,
}: FormStepProps<RecoveryPhraseType>) {
  const { t } = useTranslation();
  const { toast } = useToast();

  // `formData` is ALWAYS supplied by Wizard (it starts life as `{}`), so an
  // optional chain that stops at `formData` guards nothing — the words key is
  // what may be missing, e.g. when this step is reached before WordsStep has
  // finished generating. Chain past the cast so an absent phrase degrades to
  // `[]` (which can never match what the user types) instead of throwing
  // during render.
  const originalPhrase =
    (formData?.words as { words?: string[] } | undefined)?.words ?? [];
  const { password, oldPassword, username } = (formData?.userData ?? {}) as {
    password?: string;
    oldPassword?: string;
    username?: string;
  };

  async function onSubmit(recoveryPhrase: RecoveryPhraseType) {
    const enteredWords = recoveryPhrase.phrase
      .map((word) => word.toLowerCase())
      .join(" ");
    if (!oldPassword || !password || !username) {
      toast({
        duration: 1000,
        description: t("errorOccurred"),
        variant: "destructive",
      });
      return;
    }
    try {
      await encryptDataWithNewPassword(oldPassword, password, enteredWords);
      onClose?.();
      toast({
        duration: 1000,
        description: t("changePassword.success"),
      });
    } catch (error) {
      toast({
        duration: 1000,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    }
  }

  return (
    <BaseConfirmPhraseStep
      title={t("recover.wordsContainerTitle")}
      description={t("recover.confirmPhraseDescription", {
        wordsCount: PHRASE_WORDS_COUNT,
      })}
      onSubmit={onSubmit}
      onPrevious={onPrevious}
      onClose={onClose}
      validator={{
        validate: validateExactMatch,
        validationParams: originalPhrase,
      }}
    />
  );
}
